import { Actions } from "../actions/loan.action";
import {
  AuditTrailResponse,
  CommercialLoanDetails,
  CommercialODLoanStatement,
  CommercialODTransactionStatement,
  CreditAccountSummaryDetails,
  DrawdownLoan,
  DrawdownLoans,
  LoanDetails,
  Payout,
  RepaymentCommitment,
  RepaymentSchedule,
  RetailODDetails,
  RetailOdDrawdownLoanRepaymentSplit,
  RetailOdLedgerInfoDto,
  SubLoanLedgerInfo,
  SubLoanRepaymentSchedule,
  TermLoanDetails,
  TermLoanLedgerInfoDto,
  TermLoanRepaymentSplitDetails,
  UserLoanDetails,
  WCTLLoanRepaymentSchedule,
  WCTLoanDetail,
} from "../dto";
import { LoanType, SearchFilter, Transaction } from "../models";
import { addToLoadingQueue, removeFromLoadingQueue } from "../utils";

export interface LoanState {
  loadingQueue: string[];
  loans: TermLoanDetails[] | RetailODDetails[];
  commercialOdLoans: CommercialLoanDetails[] | null;
  workingCapitalTermLoans: WCTLoanDetail[] | null;
  singleLoan: LoanDetails | null;
  singleCommercialLoan: CommercialLoanDetails | null;
  singleLoanRepaymentSchedule: RepaymentSchedule[];
  singleLoanTransactions: Transaction[];
  commercialODLoanStatement: CommercialODLoanStatement[] | null;
  commercialODTransactionStatement: CommercialODTransactionStatement[] | null;
  termLoanRepaymentSplit: TermLoanRepaymentSplitDetails[] | null;
  userLoans: UserLoanDetails;
  userAuditTrail: AuditTrailResponse | null;
  userAuditTrailError: string | null;
  termLoanLedgerInfo: TermLoanLedgerInfoDto | null;
  retailOdLedgerInfo: RetailOdLedgerInfoDto | null;
  wctlLoan: WCTLoanDetail | null;
  wctlLoanRepaymentSchedule: WCTLLoanRepaymentSchedule[];
  paginationCount: {
    termLoanCount: number | null;
    retailODCount: number | null;
  };
  searchFilters: Record<LoanType, SearchFilter[]>;
  count: number | null;
  termLoanCreditAccountSummary: CreditAccountSummaryDetails | null;
  retailOdDrawdownLoanCreditAccountSummary: CreditAccountSummaryDetails | null;
  enterpriseLoanPageNumberLimit: {
    commercialOdLoanPageNumberLimit: number | null;
    workingCapitalTermLoanPageNumberLimit: number | null;
  };
  drawdownLoans: DrawdownLoans | null;
  singleDrawdownLoan: DrawdownLoan | null;
  payout: Payout | null;
  subLoanLedgerInfo: SubLoanLedgerInfo | null;
  retailOdDrawdownLoanRepaymentSplit:
    | RetailOdDrawdownLoanRepaymentSplit[]
    | null;
  subLoanRepaymentSchedule: SubLoanRepaymentSchedule | null;
  unpaidRepaymentCommitmentAmount: number | null;
  repaymentCommitments: RepaymentCommitment[] | null;
}

const defaultState: LoanState = {
  loadingQueue: [],
  loans: [],
  commercialOdLoans: null,
  workingCapitalTermLoans: null,
  singleLoan: null,
  singleCommercialLoan: null,
  singleLoanRepaymentSchedule: [],
  singleLoanTransactions: [],
  wctlLoanRepaymentSchedule: [],
  commercialODLoanStatement: null,
  commercialODTransactionStatement: null,
  termLoanRepaymentSplit: null,
  termLoanLedgerInfo: null,
  retailOdLedgerInfo: null,
  wctlLoan: null,
  userLoans: {
    retailODs: [],
    termLoans: [],
  },
  userAuditTrail: null,
  userAuditTrailError: null,
  searchFilters: {
    TERM_LOAN: [],
    RETAIL_OD: [],
    COMMERCIAL_OD: [],
    WORKING_CAPITAL_TERM_LOAN: [],
  },
  paginationCount: {
    termLoanCount: null,
    retailODCount: null,
  },
  count: null,
  termLoanCreditAccountSummary: null,
  retailOdDrawdownLoanCreditAccountSummary: null,
  enterpriseLoanPageNumberLimit: {
    commercialOdLoanPageNumberLimit: null,
    workingCapitalTermLoanPageNumberLimit: null,
  },
  drawdownLoans: null,
  singleDrawdownLoan: null,
  payout: null,
  subLoanLedgerInfo: null,
  retailOdDrawdownLoanRepaymentSplit: null,
  subLoanRepaymentSchedule: null,
  unpaidRepaymentCommitmentAmount: null,
  repaymentCommitments: null,
};

export default function LoanStateReducer(
  state: LoanState = defaultState,
  action: any,
): LoanState {
  switch (action.type) {
    case Actions.REQUEST_GET_PAGINATED_LOANS:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_PAGINATED_LOANS}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: action?.payload,
      };
    case `${Actions.REQUEST_GET_PAGINATED_LOANS}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: [],
      };
    case Actions.REQUEST_GET_LOAN_BY_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_LOAN_BY_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoan: action?.payload,
      };
    case `${Actions.REQUEST_GET_LOAN_BY_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoan: null,
      };
    case Actions.REQUEST_GET_LOANS_BY_USER_ID:
    case Actions.REQUEST_GET_LOANS_BY_ENTERPRISE_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case Actions.REQUEST_GET_USER_AUDIT_TRAIL:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
        userAuditTrail: null,
        userAuditTrailError: null,
      };
    case Actions.REQUEST_GET_COMMERCIAL_LOAN_BY_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_LOAN_BY_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleCommercialLoan: action?.payload,
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_LOAN_BY_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleCommercialLoan: null,
      };
    case `${Actions.REQUEST_GET_LOANS_BY_USER_ID}_SUCCESS`:
    case `${Actions.REQUEST_GET_LOANS_BY_ENTERPRISE_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        userLoans: action?.payload,
      };
    case `${Actions.REQUEST_GET_LOANS_BY_USER_ID}_FAILURE`:
    case `${Actions.REQUEST_GET_LOANS_BY_ENTERPRISE_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        userLoans: {
          retailODs: [],
          termLoans: [],
        },
      };
    case `${Actions.REQUEST_GET_USER_AUDIT_TRAIL}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        userAuditTrail: action?.payload,
        userAuditTrailError: null,
      };
    case `${Actions.REQUEST_GET_USER_AUDIT_TRAIL}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        userAuditTrail: null,
        userAuditTrailError: "Unable to load borrower audit trail",
      };
    //TODO: Payment and Withdrawal
    case Actions.REQUEST_GET_TRANSFORMED_REPAYMENT_SCHEDULE_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_TRANSFORMED_REPAYMENT_SCHEDULE_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanRepaymentSchedule: action?.payload,
      };
    case `${Actions.REQUEST_GET_TRANSFORMED_REPAYMENT_SCHEDULE_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanRepaymentSchedule: [],
      };
    case Actions.REQUEST_GET_LOAN_COUNT:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_LOAN_COUNT}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        count: action?.payload,
      };
    case `${Actions.REQUEST_GET_LOAN_COUNT}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        count: null,
      };
    case Actions.REQUEST_GET_LOAN_SEARCH_FILTERS:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_LOAN_SEARCH_FILTERS}_SUCCESS`: {
      const { loanType } = action?.metadata || {};
      if (!loanType) return state;
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        searchFilters: Object.assign({}, state.searchFilters, {
          [loanType]: action?.payload?.filters || [],
        }),
      };
    }
    case `${Actions.REQUEST_GET_LOAN_SEARCH_FILTERS}_FAILURE`: {
      const { loanType } = action?.metadata || {};
      if (!loanType) return state;
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        searchFilters: Object.assign({}, state.searchFilters, {
          [loanType]: [],
        }),
      };
    }
    case Actions.REQUEST_GET_LOANS_BY_SEARCH:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_LOANS_BY_SEARCH}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: action?.payload,
      };
    case `${Actions.REQUEST_GET_LOANS_BY_SEARCH}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: [],
      };
    case Actions.REQUEST_GET_COMMERICIAL_LOANS_BY_SEARCH:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_COMMERICIAL_LOANS_BY_SEARCH}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        commercialOdLoans: action?.payload?.data ?? [],
        enterpriseLoanPageNumberLimit: {
          ...state.enterpriseLoanPageNumberLimit,
          commercialOdLoanPageNumberLimit:
            action?.payload?.metadata?.pageNumberLimit,
        },
      };
    case `${Actions.REQUEST_GET_COMMERICIAL_LOANS_BY_SEARCH}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: [],
        commercialOdLoans: null,
        enterpriseLoanPageNumberLimit: {
          ...state.enterpriseLoanPageNumberLimit,
          commercialOdLoanPageNumberLimit: null,
        },
      };
    case Actions.REQUEST_GET_WORKING_CAPITAL_TERM_LOAN_BY_SEARCH:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
        loans: [],
      };
    case `${Actions.REQUEST_GET_WORKING_CAPITAL_TERM_LOAN_BY_SEARCH}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        workingCapitalTermLoans: action?.payload?.data ?? [],
        enterpriseLoanPageNumberLimit: {
          ...state.enterpriseLoanPageNumberLimit,
          workingCapitalTermLoanPageNumberLimit:
            action?.payload?.metadata?.pageNumberLimit,
        },
      };
    case `${Actions.REQUEST_GET_WORKING_CAPITAL_TERM_LOAN_BY_SEARCH}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        loans: [],
        workingCapitalTermLoans: null,
        enterpriseLoanPageNumberLimit: {
          ...state.enterpriseLoanPageNumberLimit,
          workingCapitalTermLoanPageNumberLimit: null,
        },
      };
    case Actions.REQUEST_GET_LOAN_PAGINATION_COUNT:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_LOAN_PAGINATION_COUNT}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        paginationCount: {
          ...state.paginationCount,
          ...action?.payload,
        },
      };
    case `${Actions.REQUEST_GET_LOAN_PAGINATION_COUNT}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case Actions.REQUEST_ADD_LOAN_PAYMENT:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_ADD_LOAN_PAYMENT}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanTransactions: [
          ...state.singleLoanTransactions,
          action?.payload,
        ],
      };
    case `${Actions.REQUEST_ADD_LOAN_PAYMENT}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case Actions.REQUEST_ADD_LOAN_WITHDRAWAL:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_ADD_LOAN_WITHDRAWAL}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanTransactions: [
          ...state.singleLoanTransactions,
          action?.payload,
        ],
      };
    case `${Actions.REQUEST_ADD_LOAN_WITHDRAWAL}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };
    case Actions.REQUEST_GET_TRANSACTIONS_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_TRANSACTIONS_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanTransactions: action?.payload,
      };
    case `${Actions.REQUEST_GET_TRANSACTIONS_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoanTransactions: [],
      };
    case Actions.REQUEST_GET_COMMERCIAL_LOAN_STATEMENT_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_LOAN_STATEMENT_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        commercialODLoanStatement: action?.payload,
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_LOAN_STATEMENT_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        commercialODLoanStatement: [],
      };

    case Actions.REQUEST_GET_COMMERCIAL_OD_TRANSACTION_STATEMENT:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_OD_TRANSACTION_STATEMENT}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        commercialODTransactionStatement: action?.payload,
      };
    case `${Actions.REQUEST_GET_COMMERCIAL_OD_TRANSACTION_STATEMENT}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        commercialODTransactionStatement: [],
      };
    case Actions.REQUEST_ADD_LOAN_DISBURSAL:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_ADD_LOAN_DISBURSAL}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleLoan: action?.payload,
      };
    case `${Actions.REQUEST_ADD_LOAN_DISBURSAL}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
      };

    case Actions.REQUEST_GET_TERM_LOAN_REPAYMENT_SPLIT_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_REPAYMENT_SPLIT_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanRepaymentSplit: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_REPAYMENT_SPLIT_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanRepaymentSplit: null,
      };
    case Actions.REQUEST_GET_TERM_LOAN_LEDGER_INFO_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_LEDGER_INFO_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanLedgerInfo: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_LEDGER_INFO_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanLedgerInfo: null,
      };
    case Actions.REQUEST_GET_RETAIL_OD_LEDGER_INFO_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_LEDGER_INFO_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdLedgerInfo: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_LEDGER_INFO_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdLedgerInfo: null,
      };
    case Actions.REQUEST_GET_WCTL_LOAN_DETAILS:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_WCTL_LOAN_DETAILS}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        wctlLoan: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_WCTL_LOAN_DETAILS}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        wctlLoan: null,
      };
    case Actions.REQUEST_WCTL_REPAYMENT_SCHEDULE:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_WCTL_REPAYMENT_SCHEDULE}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        wctlLoanRepaymentSchedule: action?.payload ?? [],
      };
    case `${Actions.REQUEST_WCTL_REPAYMENT_SCHEDULE}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        wctlLoanRepaymentSchedule: [],
      };
    case Actions.REQUEST_GET_TERM_LOAN_CREDIT_ACCOUNT_SUMMARY_BY_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_CREDIT_ACCOUNT_SUMMARY_BY_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanCreditAccountSummary: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_TERM_LOAN_CREDIT_ACCOUNT_SUMMARY_BY_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        termLoanCreditAccountSummary: null,
      };
    case Actions.REQUEST_GET_DRAWDOWN_LOANS_BY_SEARCH:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_DRAWDOWN_LOANS_BY_SEARCH}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        drawdownLoans: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_DRAWDOWN_LOANS_BY_SEARCH}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        drawdownLoans: null,
      };
    case Actions.REQUEST_GET_DRAWDOWN_LOAN_BY_DRAWDOWN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_DRAWDOWN_LOAN_BY_DRAWDOWN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleDrawdownLoan: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_DRAWDOWN_LOAN_BY_DRAWDOWN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        singleDrawdownLoan: null,
      };
    case Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_CREDIT_ACCOUNT_SUMMARY_BY_DRAWDOWN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_CREDIT_ACCOUNT_SUMMARY_BY_DRAWDOWN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdDrawdownLoanCreditAccountSummary: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_CREDIT_ACCOUNT_SUMMARY_BY_DRAWDOWN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdDrawdownLoanCreditAccountSummary: null,
      };
    case Actions.REQUEST_GET_PAYOUT:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_PAYOUT}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        payout: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_PAYOUT}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        payout: null,
      };
    case Actions.REQUEST_GET_SUB_LOAN_LEDGER_INFO_BY_SUB_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_SUB_LOAN_LEDGER_INFO_BY_SUB_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        subLoanLedgerInfo: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_SUB_LOAN_LEDGER_INFO_BY_SUB_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        subLoanLedgerInfo: null,
      };
    case Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_LOAN_REPAYMENT_SPLIT_BY_SUB_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_LOAN_REPAYMENT_SPLIT_BY_SUB_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdDrawdownLoanRepaymentSplit: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_RETAIL_OD_DRAWDOWN_LOAN_REPAYMENT_SPLIT_BY_SUB_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        retailOdDrawdownLoanRepaymentSplit: null,
      };
    case Actions.REQUEST_GET_SUB_LOANS_TRANSFORMED_REPAYMENT_SCHEDULE_BY_SUB_LOAN_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_SUB_LOANS_TRANSFORMED_REPAYMENT_SCHEDULE_BY_SUB_LOAN_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        subLoanRepaymentSchedule: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_SUB_LOANS_TRANSFORMED_REPAYMENT_SCHEDULE_BY_SUB_LOAN_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        subLoanRepaymentSchedule: null,
      };
    case Actions.REQUEST_GET_UNPAID_REPAYMENT_COMMITMENT_AMOUNT_BY_ENTITY_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_UNPAID_REPAYMENT_COMMITMENT_AMOUNT_BY_ENTITY_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        unpaidRepaymentCommitmentAmount: action?.payload ?? null,
      };
    case `${Actions.REQUEST_GET_UNPAID_REPAYMENT_COMMITMENT_AMOUNT_BY_ENTITY_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        unpaidRepaymentCommitmentAmount: null,
      };
    case Actions.REQUEST_GET_REPAYMENT_COMMITMENTS_BY_ENTITY_ID:
      return {
        ...state,
        loadingQueue: addToLoadingQueue(state.loadingQueue, action.type),
      };
    case `${Actions.REQUEST_GET_REPAYMENT_COMMITMENTS_BY_ENTITY_ID}_SUCCESS`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        repaymentCommitments: action?.payload ?? [],
      };
    case `${Actions.REQUEST_GET_REPAYMENT_COMMITMENTS_BY_ENTITY_ID}_FAILURE`:
      return {
        ...state,
        loadingQueue: removeFromLoadingQueue(state.loadingQueue, action.type),
        repaymentCommitments: null,
      };
    default:
      return state;
  }
}
